// @flow

import {Component} from 'react';

type SyncHeightRenderProps = {
    setFixedRef: (el: ?HTMLElement) => void,
    setScrolledRef: (el: ?HTMLElement) => void,
};

type Props = {
    children: (props: SyncHeightRenderProps) => React$Node,
};

class SyncHeightClass extends Component<Props> {
    fixedEl: ?HTMLElement = null;
    scrolledEl: ?HTMLElement = null;
    rafId: AnimationFrameID;
    resizeObserver: ?ResizeObserver = null;

    setFixedRef = (el: ?HTMLElement): void => {
        if (el && this.fixedEl !== el) {
            this.fixedEl = el;
            this.syncHeight();
        }
    };

    setScrolledRef = (el: ?HTMLElement): void => {
        if (el && this.scrolledEl !== el) {
            if (this.resizeObserver && this.scrolledEl) {
                this.resizeObserver.unobserve(this.scrolledEl);
            }

            this.scrolledEl = el;
            this.ensureObserver();
            if (this.resizeObserver && this.scrolledEl) {
                this.resizeObserver.observe(this.scrolledEl);
            }
            this.syncHeight();
        }
    };

    ensureObserver(): void {
        if (!this.resizeObserver && typeof ResizeObserver !== 'undefined') {
            this.resizeObserver = new ResizeObserver(() => {
                if (this.rafId) {
                    cancelAnimationFrame(this.rafId);
                }
                this.rafId = requestAnimationFrame(() => this.syncHeight());
            });
        }
    }

    syncHeight(): void {
        const fixed: ?HTMLElement = this.fixedEl;
        const scrolled: ?HTMLElement = this.scrolledEl;

        if (!fixed || !scrolled) {
            return;
        }

        const h: number = scrolled.clientHeight || 0;

        if (fixed.style.height !== `${h}px`) {
            fixed.style.height = `${h}px`;
            scrolled.style.height = `${h}px`;
        }
    }

    componentWillUnmount(): void {
        if (this.rafId) {
            cancelAnimationFrame(this.rafId);
        }
        if (this.resizeObserver && this.scrolledEl) {
            this.resizeObserver.unobserve(this.scrolledEl);
        }
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
    }

    render() {
        return this.props.children({
            setFixedRef: this.setFixedRef,
            setScrolledRef: this.setScrolledRef,
        });
    }
}

export const SyncHeight: React$ComponentType<Props> = SyncHeightClass;
